// Compact account control shown in AppHeader. When signed in: avatar circle
// with the email's first letter; tap opens a small popover with the email and
// a Sign out button. When signed out (skipAuth path): a "Sign in" pill that
// drops the skip flag so the auth gate reasserts and shows SignInScreen.

import { useEffect, useRef, useState } from 'react';
import { BarChart3, LogOut, Settings as SettingsIcon } from 'lucide-react';
import type { AuthUser } from '@/lib/auth/auth';
import { isAdminEmail } from '@/features/admin/api';
import { C } from '@/styles/tokens';

interface UserMenuProps {
  user: AuthUser | null;
  onSignOut: () => void;
  onSignIn: () => void;
  onOpenSettings: () => void;
  onOpenAdmin: () => void;
}

export function UserMenu({ user, onSignOut, onSignIn, onOpenSettings, onOpenAdmin }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) {
    // Anonymous (skipAuth) — gear icon opens Settings, where Sign in lives.
    void onSignIn; // kept for API compatibility; Settings handles sign-in now.
    return (
      <button
        type="button"
        onClick={onOpenSettings}
        aria-label="Settings"
        className="w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
        style={{ background: 'rgba(26,22,20,0.06)', color: C.ink }}
      >
        <SettingsIcon size={16} aria-hidden />
      </button>
    );
  }

  const initial = (user.email ?? user.displayName ?? '?').trim()[0]?.toUpperCase() ?? '?';

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="w-9 h-9 rounded-full flex items-center justify-center ff-body font-bold text-[13px] text-white transition-transform active:scale-95"
        style={{
          background: C.ink,
          boxShadow: '0 2px 6px rgba(26,22,20,0.18)',
        }}
      >
        {initial}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 min-w-[220px] rounded-2xl overflow-hidden anim-scale-in"
          style={{
            background: 'white',
            boxShadow: '0 8px 24px rgba(26,22,20,0.18)',
            border: '1px solid rgba(0,0,0,0.06)',
            transformOrigin: 'top right',
            zIndex: 50,
          }}
        >
          <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <div className="ff-body text-[10px] uppercase tracking-[0.2em]" style={{ color: C.inkSofter }}>
              Signed in as
            </div>
            <div className="ff-body text-[12px] font-semibold mt-0.5 truncate" style={{ color: C.ink }}>
              {user.email ?? user.displayName ?? user.id}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onOpenSettings();
            }}
            className="w-full px-4 py-3 text-left ff-body text-[13px] font-semibold inline-flex items-center gap-2 transition-colors hover:bg-black/5"
            style={{ color: C.ink, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
          >
            <SettingsIcon size={14} aria-hidden /> Settings
            <span className="ff-zh-serif opacity-70 ml-auto">设置</span>
          </button>
          {isAdminEmail(user.email) && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenAdmin();
              }}
              className="w-full px-4 py-3 text-left ff-body text-[13px] font-semibold inline-flex items-center gap-2 transition-colors hover:bg-black/5"
              style={{ color: C.ink, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
            >
              <BarChart3 size={14} aria-hidden /> Admin dashboard
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="w-full px-4 py-3 text-left ff-body text-[13px] font-semibold inline-flex items-center gap-2 transition-colors hover:bg-black/5"
            style={{ color: C.vermillionDeep }}
          >
            <LogOut size={14} aria-hidden /> Sign out
            <span className="ff-zh-serif opacity-70 ml-auto">退出</span>
          </button>
        </div>
      )}
    </div>
  );
}
